import { useEffect, useRef } from 'react';
import { navigate } from 'vike/client/router';

import { request } from '@/renderer/utils/api';
import { IS_SERVER } from '@/renderer/utils/ssr';
import useMe from '@/renderer/utils/useMe';

const REGISTER_REQUEST = {
  input: '/api/v1/deviceToken',
  method: 'POST' as const,
};

const PLATFORM_BY_CAPACITOR: Record<string, 'ios' | 'android' | 'web'> = {
  ios: 'ios',
  android: 'android',
  web: 'web',
};

// The web/JS bundle date baked into this build — the exact value the client
// sends in the `x-ras-cd` header. On native this is the OTA-updatable JS layer;
// reporting it per-device lets support see the loaded bundle, not just the shell.
const WEB_BUNDLE = `${import.meta.env.VITE_PUBLIC_ENV__APP_DATE ?? ''}`;

/**
 * Gather this device's running-version signals for the device-token report
 * (#1530): the web bundle date (always), plus the native shell build/version
 * and any applied OTA bundle when running inside Capacitor. Best-effort — any
 * piece that can't be read is simply omitted, and web/PWA reports only the
 * web bundle.
 */
async function getVersionReport(): Promise<{
  appVersion?: string;
  nativeBuild?: string;
  nativeVersion?: string;
  otaBundle?: string;
}> {
  const report: {
    appVersion?: string;
    nativeBuild?: string;
    nativeVersion?: string;
    otaBundle?: string;
  } = {};

  if (WEB_BUNDLE) {
    report.appVersion = WEB_BUNDLE;
  }

  try {
    // Lazy import for the same reason as the push plugin below: the chunk only
    // loads inside the native build, so the web client never pulls it in.
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    if (info?.build) {
      report.nativeBuild = `${info.build}`;
    }
    if (info?.version) {
      report.nativeVersion = `${info.version}`;
    }
  } catch {
    // Not native, or @capacitor/app unavailable — leave native fields unset.
  }

  try {
    // Capgo OTA (when a self-hosted live-update layer is present) exposes the
    // currently-applied bundle. It isn't wired into this build yet, so this is a
    // defensive read of the global the plugin registers — resolves to nothing
    // today and starts populating for free once OTA ships.
    const updater = (
      window as unknown as {
        Capacitor?: { Plugins?: { CapacitorUpdater?: { current?: () => Promise<unknown> } } };
      }
    ).Capacitor?.Plugins?.CapacitorUpdater;
    if (updater?.current) {
      const current = (await updater.current()) as
        | { bundle?: { version?: string; id?: string } }
        | undefined;
      const bundle = current?.bundle?.version || current?.bundle?.id;
      if (bundle && bundle !== 'builtin') {
        report.otaBundle = `${bundle}`;
      }
    }
  } catch {
    // No OTA layer — leave otaBundle unset.
  }

  return report;
}

/**
 * Registers the device for native push notifications and POSTs the resulting
 * token to the server. Idempotent — re-registers on every login / app launch
 * so the server's lastSeenAt stays fresh and revoked tokens get reactivated
 * if the user reinstalls.
 *
 * No-op outside Capacitor (web browsers don't get a token from this plugin),
 * and no-op while the user isn't logged in.
 *
 * Permission UX is intentionally implicit here: the OS prompt fires on first
 * launch after auth. If you want a deferred prompt with a custom intro UI, do
 * it elsewhere and call `requestPermissions()` from this module.
 */
export default function usePushRegistration() {
  const me = useMe();
  const userId = me?.id;
  const hasRegisteredFor = useRef<number | null>(null);

  useEffect(() => {
    if (IS_SERVER) {
      return;
    }
    if (!userId) {
      return;
    }
    // Already attempted registration for this user in this session — skip.
    if (hasRegisteredFor.current === userId) {
      return;
    }

    // Only the Capacitor build exposes window.Capacitor.isNativePlatform().
    // On the regular web client, this returns false and we bail out.
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
      .Capacitor;
    if (!cap?.isNativePlatform?.()) {
      return;
    }

    hasRegisteredFor.current = userId;

    let removeRegistrationListener: (() => void) | undefined;
    let removeErrorListener: (() => void) | undefined;
    let removeActionListener: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        // Lazy dynamic import so Vite emits it as its own chunk: the native build
        // bundles + loads it here, while the web build only fetches the chunk if
        // this code runs — and the isNativePlatform() guard above means it never
        // does on web. (Must be a real import specifier, not a Function() dodge,
        // or the native runtime has no emitted module to resolve.)
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const permission = await PushNotifications.checkPermissions();
        let granted = permission.receive === 'granted';

        if (!granted && permission.receive === 'prompt') {
          const result = await PushNotifications.requestPermissions();
          granted = result.receive === 'granted';
        }

        if (!granted) {
          // User denied permission — nothing more to do until they enable it
          // in OS settings. Clearing the ref lets us re-attempt on next mount.
          hasRegisteredFor.current = null;
          return;
        }

        const registrationHandle = await PushNotifications.addListener(
          'registration',
          async (tokenInfo) => {
            if (cancelled || !tokenInfo?.value) {
              return;
            }

            const platform =
              (cap as { getPlatform?: () => string }).getPlatform?.() === 'android'
                ? 'android'
                : 'ios';

            try {
              const version = await getVersionReport();
              await request(REGISTER_REQUEST, {
                hideAlert: true,
                body: {
                  data: {
                    token: tokenInfo.value,
                    platform: PLATFORM_BY_CAPACITOR[platform] || 'ios',
                    // App-version signals (#1530) so support can identify the
                    // build this device is on.
                    ...version,
                  },
                },
              });
            } catch (err) {
              // Token registration failed (network, auth). The next mount will
              // retry. Don't surface — push is non-essential to app function.
              console.warn('push: failed to POST device token', err);
            }
          },
        );
        removeRegistrationListener = () => registrationHandle.remove();

        const errorHandle = await PushNotifications.addListener('registrationError', (err) => {
          console.warn('push: APNs/FCM registration error', err);
        });
        removeErrorListener = () => errorHandle.remove();

        // Deep-link on tap: the server tags each push with `data.url` (e.g.
        // /support-chat/me for a DM, /release-center for a release/update). Only
        // follow internal paths, never an arbitrary URL from the payload.
        const actionHandle = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            const url = (action?.notification?.data as { url?: string } | undefined)?.url;
            if (typeof url === 'string' && url.startsWith('/')) {
              navigate(url);
            }
          },
        );
        removeActionListener = () => actionHandle.remove();

        await PushNotifications.register();
      } catch (err) {
        console.warn('push: registration setup failed', err);
        hasRegisteredFor.current = null;
      }
    })();

    return () => {
      cancelled = true;
      removeRegistrationListener?.();
      removeErrorListener?.();
      removeActionListener?.();
    };
  }, [userId]);
}
